import add from 'date-fns/add';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { NavigateFunction } from 'react-router';
import { IUser, WorkspaceUser } from 'src/@types/user';

import { SettingsValueProps } from 'src/components/settings/types';
import {
  LoginResponse,
  RegisterResponse,
  UserRequest,
} from 'src/requests/user';
import { Analytics } from 'src/services/analytics';
import { Sentry } from 'src/services/sentry';
import { Session } from '../state/session';
import { openAppAsUser, postLogin } from '../utils/user-utils';

import { googleLogout } from '@react-oauth/google';
import { WorkspaceRequests } from 'src/requests/workspace.requests';
import { getToken, startSessionFromLogin } from 'src/state';
import { routes } from '../Router';
import {
  ActionMapType,
  AuthStateType,
  AuthUserType,
  JWTContextType,
} from './types';

enum Types {
  INITIAL = 'INITIAL',
  LOGIN_GUEST = 'LOGIN_GUEST',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  REFRESH = 'REFRESH',
  REGISTER = 'REGISTER',
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN_GUEST]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
  [Types.REFRESH]: {
    user: AuthUserType;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN_GUEST) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REFRESH) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  return state;
};

export const AuthContext = createContext<JWTContextType | null>(null);

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // When page load
  const initialize = useCallback(async () => {
    try {
      if (sessionStorage.getItem('accessToken')) {
        sessionStorage.removeItem('accessToken');
      }
      const token = getToken();

      if (token) {
        const user = await UserRequest.getInfo();

        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
        Sentry.identify(user);
        Sentry.capture('auth', 'info', 'restore session');
        Analytics.identity(user);
        setLoggedCookie(true);
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
        setLoggedCookie(false);
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
      setLoggedCookie(false);
    }
  }, []);

  // GUEST LOGIN
  const loginGuest = useCallback(async (user: WorkspaceUser) => {
    dispatch({
      type: Types.LOGIN_GUEST,
      payload: {
        user: { ...user, isGuest: true } as IUser,
      },
    });
    Sentry.identify(user as IUser);
    Sentry.capture('auth', 'info', 'GUEST User logged in');
    Analytics.identifyGatheringSpaceGuest(user);
    Analytics.capture('guest_login');
    setLoggedCookie(true);
  }, []);

  // LOGIN
  const loginUser = useCallback(async (user: IUser) => {
    Sentry.identify(user);
    dispatch({ type: Types.LOGIN, payload: { user } });
    Sentry.capture('auth', 'info', 'User logged in');
    Analytics.identity(user);
    Analytics.capture('user_login');
    setLoggedCookie(true);
  }, []);

  const joinWorkspaceByInvite = useCallback(
    async (workspaceInvite: string, replacePath?: boolean) => {
      const { newUser, accessTokens } =
        await WorkspaceRequests.joinWorkspaceByInvite(workspaceInvite);

      if (accessTokens) startSessionFromLogin(accessTokens);

      if (accessTokens?.user) await loginUser(accessTokens.user);
      Analytics.capture('workspace_join');

      if (newUser?._id) openAppAsUser(newUser._id, replacePath);
    },
    [loginUser],
  );

  const registerUser = useCallback(
    async ({
      name,
      email,
      password,
      workspaceInvite,
      navigate,
      humanKey,
    }: {
      name: string;
      email: string;
      password: string;
      workspaceInvite?: string;
      navigate?: NavigateFunction;
      humanKey: string;
    }) => {
      const registerResponse = await UserRequest.register({
        name,
        email,
        password,
        workspaceInvite,
        humanKey,
      });

      Analytics.capture('user_register', {
        source: workspaceInvite ? 'invite' : undefined,
      });

      if ((registerResponse as RegisterResponse).needVerifyEmail) {
        return (registerResponse as RegisterResponse).needVerifyEmail;
      }
      if ((registerResponse as LoginResponse).accessTokens) {
        await postLogin(registerResponse as LoginResponse, {
          navigate,
          successCallback: async (user) => {
            await loginUser(user);
          },
        });
        return false;
      }
      return true;
    },
    [loginUser],
  );

  const resetPassword = useCallback(
    async (code: string, password: string, navigate?: NavigateFunction) => {
      const registerResponse = await UserRequest.resetPassword({
        code,
        password,
      });

      if (registerResponse.accessTokens) {
        await postLogin(registerResponse, {
          navigate,
          successCallback: async (user) => {
            await loginUser(user);
          },
        });
        return false;
      }
      return true;
    },
    [loginUser],
  );

  const forgotPassword = useCallback(async (email: string) => {
    await UserRequest.forgotPassword(email);
  }, []);

  // Update User Settings
  const updateSettings = useCallback(async (settings: SettingsValueProps) => {
    await UserRequest.updateSettings(settings);
  }, []);

  // GUEST LOGOUT
  const logoutGuest = useCallback(() => {
    setLoggedCookie(false);
    // googleLogout();
    // Session.logout();

    Analytics.capture('guest_logout');

    dispatch({
      type: Types.LOGOUT,
    });
    // window.location.replace('/login');
  }, []);

  // LOGOUT
  const logout = useCallback(
    async (workspaceInvite?: string, dontNavigate?: boolean) => {
      await UserRequest.logout();
      setLoggedCookie(false);

      googleLogout();

      Session.logout();

      Analytics.capture('user_logout');

      dispatch({
        type: Types.LOGOUT,
      });

      if (dontNavigate) return;

      const route = workspaceInvite
        ? `${routes.auth.login}?workspaceInvite=${workspaceInvite}`
        : routes.auth.login;
      window.location.replace(route);
    },
    [],
  );

  // Refresh
  const refresh = useCallback(async () => {
    try {
      const accessToken = Session.getToken();

      if (accessToken) {
        const user = await UserRequest.getInfo();

        dispatch({
          type: Types.REFRESH,
          payload: {
            user,
          },
        });

        return user;
      }
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }

    return null;
  }, []);

  // Initialize
  useEffect(() => {
    initialize();
  }, [initialize]);

  const setLoggedCookie = (value: boolean) => {
    const expireAt = add(new Date(), { months: 2 }).toUTCString();
    document.cookie = `copera_logged=${value};path=/;expires=${expireAt};domain=.copera.ai`;
  };

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      loginGuest,
      logoutGuest,
      loginUser,
      updateSettings,
      logout,
      refresh,
      registerUser,
      forgotPassword,
      resetPassword,
      joinWorkspaceByInvite,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      loginGuest,
      logoutGuest,
      loginUser,
      logout,
      updateSettings,
      refresh,
      registerUser,
      forgotPassword,
      resetPassword,
      joinWorkspaceByInvite,
    ],
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
